import { useMemo, useRef } from "react";

import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { cn } from "$/lib/utils/functions/misc.functions";

import type { DropdownProps, Option } from "./Dropdown.types";
import { WarningIcon } from "$/lib/icons/WarningIcon";
import { customColors } from "tailwind.config";
import Flexbox from "../../common/Flexbox";

const Dropdown = <T extends object | string | number>({
  label,
  hideLabel = false,
  placeholder,
  options,
  isError,
  value,
  defaultValue,
  errorMessage,
  containerClassName,
  dropdownContainerClassName,
  creatable = false,
  theme = "blue",
  formatOptionLabel,
  /* The reason I had to do it because TypeScript
    could not recognize the difference of the props: 
    `onChange`, `classNames`, and `multi` when in a Union state */
  ...restProps
}: DropdownProps<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const fullDefaultValue = useMemo(
    () => options.find((option) => option.value === defaultValue),
    [defaultValue, options],
  );

  const fullValue = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  return (
    <Flexbox className={cn("z-50", containerClassName)}>
      {!hideLabel && (
        <p
          className={cn("mb-1 ml-1 text-sm font-normal text-tertiary-600", {
            hidden: !label,
          })}
        >
          {label}
        </p>
      )}
      <div
        ref={containerRef}
        className={cn(
          "select-input group relative w-full",
          dropdownContainerClassName,
        )}
      >
        {restProps.multi ? (
          <Select<Option<T>, true>
            classNames={{
              ...restProps.classNames,
              control: (props) =>
                cn(
                  "!min-h-11 !border-0",
                  theme === "blue" && "!bg-primary-100",
                  theme === "green" && "!bg-forest-100",
                  theme === "orange" && "!bg-warning-100",
                  restProps.classNames?.control?.(props),
                ),
              placeholder: (props) =>
                cn(
                  "!text-tertiary-800",
                  restProps.classNames?.placeholder?.(props),
                ),
              indicatorSeparator: (props) =>
                cn(
                  "!hidden",
                  restProps.classNames?.indicatorSeparator?.(props),
                ),
              dropdownIndicator: (props) =>
                cn(
                  "!text-tertiary-800",
                  restProps.classNames?.dropdownIndicator?.(props),
                ),
              multiValue: (props) =>
                cn(
                  "!bg-transparent",
                  restProps.classNames?.multiValue?.(props),
                ),
              multiValueLabel: (props) =>
                cn(
                  " !font-semibold",
                  restProps.classNames?.multiValueLabel?.(props),
                ),
            }}
            onChange={restProps.onChange}
            isMulti
            components={restProps.components}
            options={options}
            formatOptionLabel={formatOptionLabel}
            defaultValue={fullDefaultValue}
            value={fullValue}
            isSearchable={false}
            placeholder={placeholder}
            menuPlacement="auto"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        ) : !creatable ? (
          <Select<Option<T>, false>
            classNames={{
              ...restProps.classNames,
              control: (props) =>
                cn(
                  "!min-h-11 !border-0",
                  theme === "blue" && "!bg-primary-100",
                  theme === "green" && "!bg-forest-100",
                  theme === "orange" && "!bg-warning-100",
                  restProps.classNames?.control?.(props),
                ),
              option(props) {
                return cn(
                  theme === "blue" &&
                    `${!props.isSelected ? "hover:!bg-primary-100 !text-primary" : ""} ${props.isSelected ? "!bg-primary" : ""}`,
                  theme === "green" &&
                    `${!props.isSelected ? "hover:!bg-forest-100 !text-forest" : ""} ${props.isSelected ? "!bg-forest" : ""}`,
                  theme === "orange" &&
                    `${!props.isSelected ? "hover:!bg-warning-100 !text-warning" : ""} ${props.isSelected ? "!bg-warning" : ""}`,
                  restProps.classNames?.option?.(props),
                );
              },
              placeholder: (props) =>
                cn(
                  "!text-tertiary-800",
                  restProps.classNames?.placeholder?.(props),
                ),
              indicatorSeparator: (props) =>
                cn(
                  "!hidden",
                  restProps.classNames?.indicatorSeparator?.(props),
                ),
              dropdownIndicator: (props) =>
                cn(
                  "!text-tertiary-800",
                  restProps.classNames?.dropdownIndicator?.(props),
                ),
            }}
            components={restProps.components}
            options={options}
            onChange={restProps.onChange}
            formatOptionLabel={formatOptionLabel}
            defaultValue={fullDefaultValue}
            value={fullValue}
            placeholder={placeholder}
            isSearchable={restProps.isSearchable}
            menuPlacement="auto"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        ) : (
          <CreatableSelect<Option<T>, false>
            classNames={{
              ...restProps.classNames,
              control: (props) =>
                cn(
                  "!bg-primary-100 !min-h-11 !border-0",
                  restProps.classNames?.control?.(props),
                ),
              placeholder: (props) =>
                cn(
                  "!text-tertiary-800",
                  restProps.classNames?.placeholder?.(props),
                ),
              indicatorSeparator: (props) =>
                cn(
                  "!hidden",
                  restProps.classNames?.indicatorSeparator?.(props),
                ),
              dropdownIndicator: (props) =>
                cn(
                  "!text-tertiary-800",
                  restProps.classNames?.dropdownIndicator?.(props),
                ),
            }}
            components={restProps.components}
            options={options}
            onChange={restProps.onChange}
            defaultValue={fullDefaultValue}
            value={fullValue}
            placeholder={placeholder}
            // isSearchable={false}
            menuPlacement="auto"
            menuPortalTarget={document.body}
            isClearable
            escapeClearsValue
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        )}

        {isError && errorMessage && (
          <p className="mt-1 flex items-center gap-1 text-xs text-error">
            <WarningIcon
              color={customColors.error.DEFAULT}
              className="size-4"
            />
            {errorMessage}
          </p>
        )}
      </div>
    </Flexbox>
  );
};

export default Dropdown;
